import type { TFunction } from 'i18next';
import { jsPDF } from 'jspdf';
import i18n from '@/i18n';
// Resolution 20 (SL-0027): the repo-root brand asset (`assets/DGBiotech-logo.png`), forced to
// inline as a base64 data URI via Vite's `?inline` suffix regardless of its size — jsPDF's
// `addImage` needs a data URI/raw bytes, never a served URL, and this is the same raster PNG the
// SVG-tracing pipeline (Task 1.4) was itself derived from.
import brandLogoDataUri from '../../../../../assets/DGBiotech-logo.png?inline';

/**
 * Client-side PDF assembly ported (and simplified) from the ~/dev/dgbiotech/progetto1 "Scheda
 * Documento" prototype. Builds the printable identity card from the transcribed fields plus the
 * rectified images, as an ephemeral Blob the browser downloads — nothing is persisted server-side.
 */
export interface SchedaCard {
  readonly tipo: string;
  readonly intestazioneNome: string;
  readonly sottotitolo: string;
  readonly anagrafici: Readonly<Record<string, string>>;
  readonly documento: ReadonlyArray<{ readonly label: string; readonly valore: string }>;
}

export interface SchedaImage {
  readonly caption: string;
  readonly dataUrl: string;
}

const BLUE: [number, number, number] = [30, 66, 76];
const LABEL_BG: [number, number, number] = [220, 230, 241];
const BORDER: [number, number, number] = [150, 160, 170];
const GREY: [number, number, number] = [89, 89, 89];
const INK: [number, number, number] = [20, 20, 20];

const PAGE_WIDTH = 210;
const PAGE_HEIGHT = 297;
const MARGIN = 14;
const CONTENT_WIDTH = PAGE_WIDTH - 2 * MARGIN;

// assets/DGBiotech-logo.png's own natural pixel dimensions (Resolution 20, confirmed at Task 1.4),
// used to keep the embedded lockup's aspect ratio correct at an arbitrary print width.
const LOGO_WIDTH_MM = 46;
const LOGO_HEIGHT_MM = LOGO_WIDTH_MM * (172 / 1320);

/** The document being written and the vertical position of the next block, in mm. */
interface Page {
  readonly doc: jsPDF;
  y: number;
}

type Translate = TFunction<'identity-documents'>;

// jsPDF types splitTextToSize as `any`; it returns string[] at runtime. Cast once, here, so the
// rest of the builder works with a properly-typed string[] (no scattered unsafe-any usage).
const splitLines = (doc: jsPDF, text: string, maxWidth: number): string[] =>
  doc.splitTextToSize(text, maxWidth) as string[];

function addLogo(page: Page): void {
  try {
    page.doc.addImage(
      brandLogoDataUri,
      'PNG',
      (PAGE_WIDTH - LOGO_WIDTH_MM) / 2,
      page.y,
      LOGO_WIDTH_MM,
      LOGO_HEIGHT_MM,
    );
  } catch {
    // A malformed/unavailable logo asset must not abort the whole PDF — proceed without it.
  }
  page.y += LOGO_HEIGHT_MM + 6;
}

function addTitleBlock(page: Page, t: Translate, card: SchedaCard): void {
  const { doc } = page;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(17);
  doc.setTextColor(...BLUE);
  doc.text(t('pdf.title'), PAGE_WIDTH / 2, page.y, { align: 'center' });
  page.y += 8;

  doc.setFontSize(13);
  doc.setTextColor(...INK);
  doc.text(card.intestazioneNome, PAGE_WIDTH / 2, page.y, { align: 'center' });
  page.y += 6;

  doc.setFont('helvetica', 'italic');
  doc.setFontSize(10.5);
  doc.setTextColor(...GREY);
  doc.text(card.sottotitolo, PAGE_WIDTH / 2, page.y, { align: 'center' });
  page.y += 2.5;

  doc.setDrawColor(...BLUE);
  doc.setLineWidth(0.6);
  doc.line(MARGIN, page.y, PAGE_WIDTH - MARGIN, page.y);
  page.y += 8;
}

function addHeading(page: Page, text: string): void {
  page.doc.setFont('helvetica', 'bold');
  page.doc.setFontSize(12);
  page.doc.setTextColor(...BLUE);
  page.doc.text(text, MARGIN, page.y);
  page.y += 2.5;
}

/** Starts a new page when the next `height` mm would run into the bottom margin. */
function ensureRoom(page: Page, height: number): void {
  if (page.y + height > PAGE_HEIGHT - MARGIN) {
    page.doc.addPage();
    page.y = MARGIN;
  }
}

function addTable(page: Page, rows: ReadonlyArray<readonly [string, string]>): void {
  const { doc } = page;
  const labelWidth = 60;
  const valueWidth = CONTENT_WIDTH - labelWidth;
  const padX = 2;
  const lineHeight = 4.4;
  doc.setFontSize(10);
  for (const [label, value] of rows) {
    doc.setFont('helvetica', 'normal');
    const valueLines = splitLines(doc, value || '—', valueWidth - 2 * padX);
    const labelLines = splitLines(doc, label, labelWidth - 2 * padX);
    const lineCount = Math.max(valueLines.length, labelLines.length, 1);
    const rowHeight = lineCount * lineHeight + 3;
    ensureRoom(page, rowHeight);
    const y = page.y;
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.2);
    doc.setFillColor(...LABEL_BG);
    doc.rect(MARGIN, y, labelWidth, rowHeight, 'FD');
    doc.rect(MARGIN + labelWidth, y, valueWidth, rowHeight, 'D');
    const textY = y + (rowHeight - lineCount * lineHeight) / 2 + lineHeight - 1.3;
    doc.setTextColor(...INK);
    doc.setFont('helvetica', 'bold');
    doc.text(labelLines, MARGIN + padX, textY);
    doc.setFont('helvetica', 'normal');
    doc.text(valueLines, MARGIN + labelWidth + padX, textY);
    page.y += rowHeight;
  }
  page.y += 4;
}

function addImages(page: Page, images: readonly SchedaImage[]): void {
  const { doc } = page;
  page.y += 2;
  const boxWidth = 85.6;
  const boxHeight = 53.98;
  const gap = 8;
  const groupWidth = images.length * boxWidth + (images.length - 1) * gap;
  const x0 = Math.max(MARGIN, (PAGE_WIDTH - groupWidth) / 2);
  ensureRoom(page, boxHeight + 8);
  const captionY = page.y + 2;
  const boxTop = page.y + 4;
  images.forEach((image, index) => {
    const bx = x0 + index * (boxWidth + gap);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...INK);
    doc.text(image.caption, bx + boxWidth / 2, captionY, { align: 'center' });
    try {
      doc.addImage(image.dataUrl, 'JPEG', bx, boxTop, boxWidth, boxHeight, undefined, 'FAST');
    } catch {
      // A malformed data URL must not abort the whole PDF — leave the box empty.
    }
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.2);
    doc.rect(bx, boxTop, boxWidth, boxHeight, 'D');
  });
  page.y = boxTop + boxHeight + 6;
}

function addFooter(page: Page, t: Translate): void {
  const { doc } = page;
  ensureRoom(page, 22);
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.2);
  doc.line(MARGIN, page.y, PAGE_WIDTH - MARGIN, page.y);
  page.y += 4;
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8);
  doc.setTextColor(...GREY);
  const footerLines = splitLines(
    doc,
    `${t('pdf.caption.privacyLabel')} – ${t('pdf.privacyDisclaimer')}`,
    CONTENT_WIDTH,
  );
  doc.text(footerLines, MARGIN, page.y);
}

export function buildSchedaPdf(card: SchedaCard, images: readonly SchedaImage[]): Blob {
  // Resolution 8: the generated identity-document PDF is a fixed-Italian regulatory artifact,
  // resolved through `getFixedT('it', ...)` regardless of the operator's active UI locale — never
  // the active-locale `t()` a component would use.
  const t = i18n.getFixedT('it', 'identity-documents');
  const page: Page = {
    doc: new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' }),
    y: 12,
  };

  addLogo(page);
  addTitleBlock(page, t, card);

  addHeading(page, t('pdf.heading.anagrafici'));
  addTable(
    page,
    Object.entries(card.anagrafici).map(([label, value]) => [label, value] as const),
  );

  addHeading(page, t('pdf.heading.documento'));
  addTable(
    page,
    card.documento.map((field) => [field.label, field.valore] as const),
  );

  if (images.length > 0) {
    addHeading(page, t('pdf.heading.immagini'));
    addImages(page, images);
  }

  addFooter(page, t);
  return page.doc.output('blob');
}
